import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { ArrowLeft, Newspaper, ChevronRight, Lock } from 'lucide-react'
import { api, type BriefingHistoryItem, type BriefingCluster } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ClusterCard } from '@/pages/feed'

export function BriefingsPage() {
  const { user } = useAuth()
  const isPaid = !!user && user.billing_plan !== 'free'
  const [history, setHistory] = useState<BriefingHistoryItem[] | null>(null)
  const [selected, setSelected] = useState<BriefingHistoryItem | null>(null)
  const [clusters, setClusters] = useState<BriefingCluster[] | null>(null)
  const [loadingBrief, setLoadingBrief] = useState(false)

  useEffect(() => {
    if (!isPaid) return
    api.getBriefingHistory().then(({ briefings }) => setHistory(briefings)).catch(() => setHistory([]))
  }, [isPaid])

  // Refetch the archive when the tab regains focus/visibility — same reason as
  // the feed: background tabs throttle timers, so a brief built while away would
  // otherwise need a hard refresh to appear.
  useEffect(() => {
    if (!isPaid) return
    const refetchIfVisible = () => {
      if (document.visibilityState !== 'visible') return
      api.getBriefingHistory().then(({ briefings }) => setHistory(briefings)).catch(() => {})
    }
    document.addEventListener('visibilitychange', refetchIfVisible)
    window.addEventListener('focus', refetchIfVisible)
    return () => {
      document.removeEventListener('visibilitychange', refetchIfVisible)
      window.removeEventListener('focus', refetchIfVisible)
    }
  }, [isPaid])

  // Paid-only: the daily brief still shows on the feed for everyone, but the
  // browsable archive is gated. Tab stays visible (per product intent) → upsell.
  if (!isPaid) {
    return (
      <div className="space-y-5 pb-12">
        <div>
          <h1 className="text-2xl font-bold">Briefings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Revisit your daily niche briefings, any day.</p>
        </div>
        <Card className="p-10 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Lock className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold">The briefing archive is a Plus feature</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            You'll still get your niche brief on the feed every day. Upgrade to browse and search every
            past briefing — what your niche said, on any day.
          </p>
          <Link to="/upgrade">
            <Button className="mt-6">Upgrade to Plus</Button>
          </Link>
        </Card>
      </div>
    )
  }

  const openBrief = async (item: BriefingHistoryItem) => {
    setSelected(item)
    setClusters(null)
    setLoadingBrief(true)
    try {
      const { clusters } = await api.getBriefing(item.id)
      setClusters(clusters)
    } catch {
      setClusters([])
    } finally {
      setLoadingBrief(false)
    }
  }

  // --- Detail view: a single past brief ---
  if (selected) {
    return (
      <div className="space-y-5 pb-12">
        <button
          type="button"
          onClick={() => { setSelected(null); setClusters(null) }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All briefings
        </button>
        <div>
          <h1 className="text-2xl font-bold">{format(new Date(selected.created_at), 'EEEE, MMMM d')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {selected.cluster_count} {selected.cluster_count === 1 ? 'topic' : 'topics'} · {selected.source_video_count} videos
          </p>
        </div>

        {loadingBrief && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
          </div>
        )}
        {!loadingBrief && clusters && clusters.length === 0 && (
          <p className="text-sm text-muted-foreground">This briefing is no longer available.</p>
        )}
        {!loadingBrief && clusters && clusters.length > 0 && (
          <div className="space-y-3">
            {clusters.map((c) => <ClusterCard key={c.id} cluster={c} />)}
          </div>
        )}
      </div>
    )
  }

  // --- List view: the archive ---
  return (
    <div className="space-y-5 pb-12">
      <div>
        <h1 className="text-2xl font-bold">Briefings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your daily niche briefings from the last 14 days. Open any day to revisit what your niche was saying.
        </p>
      </div>

      {history === null && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        </div>
      )}

      {history && history.length === 0 && (
        <Card className="p-12 text-center">
          <Newspaper className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
          <p className="text-muted-foreground">No briefings yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Once your followed channels start publishing, your daily briefings will collect here.
          </p>
        </Card>
      )}

      {history && history.length > 0 && (
        <div className="divide-y rounded-xl border">
          {history.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openBrief(item)}
              className="flex w-full items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-muted/40"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Newspaper className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{format(new Date(item.created_at), 'EEEE, MMM d')}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {item.top_title
                    ? item.top_title
                    : `${item.cluster_count} ${item.cluster_count === 1 ? 'topic' : 'topics'}`}
                  {' · '}{item.source_video_count} videos
                </p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
